import {
  Accordion,
  AccordionDetails,
  FormControl,
  FormGroup,
  FormLabel,
  styled,
  Typography,
  withStyles,
} from '@material-ui/core';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MEquipmentPool from '~/components/PF2MEquipmentPool';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import {
  deallocateAllLoadAllocations,
  getEquipmentScheduleSubelements,
  getPerforatorEquipmentScheduleList,
  setSelectedPerforatorAllocation,
} from '~/store/dispatch/actions';
import { getEquipments } from '~/store/manager/actions';
import PF2MFullScreenButton from '~/components/PF2MFullScreenButton';
import PF2MEquipmentPoolFilter from '~/components/PF2MEquipmentPoolFilter';
import { onPerforatorScheduleDelete, onPerforatorScheduleSave } from '../utils';
import styles from '../styles';
import { AllocationModal } from './AllocationModal';
import AllocationRow from './AllocationRow';
import ReloadQueue, { priority } from '../../ReloadQueue';
import { getElements, getSubElements, getEquipmentTypes } from '~/store/nref/actions';
import useTabletConfiguration from '~/services/tabletConfiguration';


const AccordionSummary = styled(props => (
  <MuiAccordionSummary
    {...props}
  />
))(() => ({
  minHeight: '10px !important',
  '& .MuiAccordionSummary-content': {
    margin: 5,
  },
}));

// const PERFORATOR = 4;
const PERFORATOR_EQUIP_IDS = [4, 11, 16, 17, 18, 19];
const PRODUCTION_EQUIP_IDS = [13, 14, 20, 21, 22, 23, 24, 25, 29];
const GENERIC_EQUIP_IDS = [5, 6, 7, 8, 9, 10, 12, 26, 27, 28, 30];

const ENABLED_EQUIP_IDS = [
  ...PERFORATOR_EQUIP_IDS,
  ...PRODUCTION_EQUIP_IDS,
  ...GENERIC_EQUIP_IDS,
];
const AllocationsTab = ({ classes }) => {
  const dispatch = useDispatch();
  const handle = useFullScreenHandle();
  const { t: translate } = useTranslation();

  const filteredEquipmentTypes = useSelector(
    state => state.nref.equipmentTypes.filter(r => ENABLED_EQUIP_IDS.includes(r.id)),
  );
  const equipmentGroups = useSelector(state => state.nref.equipmentGroups);
  const filteredEquipments = useSelector(state => state.manager.equipments.filter(
    equipment => ENABLED_EQUIP_IDS.includes(equipment.id_equip),
  ));
  const availableEquipments = filteredEquipments.map(r => ({
    ...r,
    equip_name: r.name,
    equip_type_id: r.id_equip,
  }));

  const perforatorEquipmentScheduleList = useSelector(
    state => state.dispatch.perforatorEquipmentScheduleList,
  );
  const equipmentScheduleSubelements = useSelector(
    state => state.dispatch.equipmentScheduleSubelements,
  );
  const { value: dispatchUpdateTime } = useTabletConfiguration('dispatch_update_time');

  const [poolEquipmentType, setPoolEquipmentType] = useState([]);
  const [poolEquipmentGroup, setPoolEquipmentGroup] = useState([]);
  const [poolEquipmentName, setPoolEquipmentName] = useState('');

  const [isDeallocationAlertOpen, setIsDeallocationAlertOpen] = useState(false);
  const [isPerforatorModalOpen, setIsPerforatorModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const reloadList = [
    { name: 'getPerforatorEquipmentScheduleList', action: getPerforatorEquipmentScheduleList },
    { name: 'getEquipmentScheduleSubelements', action: getEquipmentScheduleSubelements },
    { name: 'getEquipments', action: getEquipments },
    { name: 'getElements', action: getElements },
    { name: 'getSubElements', action: getSubElements },
    { name: 'getEquipmentTypes', action: getEquipmentTypes },
  ];

  useEffect(() => {
    if (dispatchUpdateTime !== undefined) {
      ReloadQueue.setUpdateTime(Number(dispatchUpdateTime), dispatch);
    }

    ReloadQueue.clearReloadList(priority.MEDIUM, dispatch);
    ReloadQueue.addToReloadList(dispatch, reloadList, priority.MEDIUM);
    return () => {
      ReloadQueue.stopReload();
    };
  }, [dispatch, dispatchUpdateTime]);

  const setEquipmentTypes = (e) => {
    setPoolEquipmentType([...e.target.value.map(r => r.value)]);
    setPoolEquipmentGroup(
      equipmentGroups.filter(
        equip => [...e.target.value.map(target => target.value)]
          .includes(equip.id_equipament),
      ).map(eg => eg.id),
    );
  };

  const renderEmptyRow = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: 90,
        backgroundColor: '#F9F9F9',
        color: '#647886',
        fontSize: '14px',
        fontStyle: 'normal',
        fontFamily: 'Roboto',
      }}
    >
      <span>{translate('common:NoRegisteredAllocations')}</span>
    </div>
  );

  const filters = React.useMemo(() => ([
    {
      type: 'multiSelect',
      label: `${translate('common:EquipmentType')}`,
      value: poolEquipmentType,
      options: filteredEquipmentTypes.map(e => ({
        value: e.id,
        label: e.name,
      })),
      onChange: e => setEquipmentTypes(e),
      placeholder: `${translate('common:Select')}`,
    },
    {
      type: 'multiSelect',
      label: `${translate('common:EquipmentGroup')}`,
      value: poolEquipmentGroup,
      options: equipmentGroups
        .filter(r => poolEquipmentType.includes(r.id_equipament))
        .map(e => ({
          value: e.id,
          label: e.name,
        })),
      onChange: e => setPoolEquipmentGroup([...e.target.value.map(r => r.value)]),
      placeholder: `${translate('common:Select')}`,
    },
    {
      type: 'textField',
      label: `${translate('common:Equipment')}`,
      value: poolEquipmentName,
      onChange: e => setPoolEquipmentName(e?.target?.value),
      placeholder: `${translate('common:Find')}`,
    },
  ]), [
    poolEquipmentType,
    poolEquipmentGroup,
    poolEquipmentName,
    filteredEquipmentTypes,
    equipmentGroups,
  ]);

  const renderPools = () => (
    <div style={{ display: 'flex', width: '100%' }}>
      <PF2MEquipmentPoolFilter filters={filters} />
      <div style={{
        display: 'flex',
        marginTop: 10,
        width: '85%',
      }}
      >
        <PF2MEquipmentPool
          equips={availableEquipments.map(r => ({ ...r, equip_id: r.id }))
            .filter(e => (
              !perforatorEquipmentScheduleList.find(pes => pes.group_equipament_links_id === e.id)
            ))
            .filter(e => (poolEquipmentType?.length
              ? poolEquipmentType.includes(e.id_equip)
              : true))
            .filter(e => (poolEquipmentGroup?.length
              ? poolEquipmentGroup.includes(e.id_group)
              : true))
            .filter((e) => {
              if (!poolEquipmentName) return true;
              return e.equip_name
                ?.toLowerCase()
                ?.includes(poolEquipmentName?.trim()?.toLowerCase());
            })
          }
          onItemDropped={(scheduleId, _type) => {
            const schedule = perforatorEquipmentScheduleList.find(({ id }) => id === scheduleId);
            if (schedule !== undefined) {
              onPerforatorScheduleDelete({
                dispatch,
                spa: schedule,
                scheduleId: schedule.id,
              });
            }
          }}
          onEquipClick={(equip) => {
            const isIncluded = ENABLED_EQUIP_IDS.includes(equip.id_equip);
            if (isIncluded) {
              dispatch(setSelectedPerforatorAllocation(equip));
              setIsPerforatorModalOpen(true);
            }
          }}
          headerText={translate('common:AvailableEquipments').toUpperCase()}
        />
      </div>
    </div>
  );

  const renderTableHeader = () => (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: 50,
      }}
    >
      <div style={{
        width: '90px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      >
        <span className={classes.labelTypeSelector}>{translate('common:Front')}</span>
      </div>
      <div style={{
        width: '65%', display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}
      >
        <span className={classes.labelTypeSelector}>{translate('common:Equipments')}</span>
      </div>
    </div>
  );

  const renderAllocationTable = () => (
    <div style={{
      flex: 1,
      width: '100%',
      overflowY: 'scroll',
    }}
    >
      {renderTableHeader()}
      <div style={{
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        transition: 'max-height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        height: 'auto',
      }}
      >
        <>
          {
            equipmentScheduleSubelements?.length
              ? equipmentScheduleSubelements.map(
                r => (
                  <AllocationRow
                    key={r.id}
                    subelement={r}
                    perforators={perforatorEquipmentScheduleList.filter(
                      x => x.flowchart_element_point_id === r.id,
                    )}
                    onPerforatorDrop={({ spa, subelement }) => {
                      if (!spa.group_equipament_links_id) {
                        onPerforatorScheduleSave({ dispatch, spa, subelement_id: subelement.id });
                      }
                    }}
                    onEquipClick={(sched) => {
                      const isIncluded = ENABLED_EQUIP_IDS
                        .includes(sched.equipaments_id);
                      if (isIncluded) {
                        const foundEquipment = availableEquipments.find(
                          e => e.id === sched.group_equipament_links_id,
                        );
                        dispatch(setSelectedPerforatorAllocation({
                          ...foundEquipment,
                          sched_id: sched.id,
                          equip_id: sched.group_equipament_links_id,
                        }));
                        setIsPerforatorModalOpen(true);
                      }
                    }}
                  />
                ),
              )
              : renderEmptyRow()
          }
        </>
      </div>
    </div>
  );

  const renderAvailableEquipments = () => (
    <Accordion
      defaultExpanded
      onChange={() => setExpanded(!expanded)}
      expanded={expanded}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        id="1"
      >
        <Typography>{translate('common:AvailableEquipments')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {renderPools()}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <FullScreen handle={handle}>
      <div style={handle.active ? { height: '100vh' } : null} className={classes.tabContainer}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PF2MFullScreenButton
            isFullScreenActive={handle.active}
            open={handle.enter}
            close={handle.exit}
            variant="outlined"
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: handle.active ? '95%' : '82vh',
          }}
        >
          {renderAllocationTable()}
          {handle.active ? null : renderAvailableEquipments()}
        </div>
      </div>
      <AllocationModal
        modalData={isPerforatorModalOpen}
        closeModal={() => setIsPerforatorModalOpen(false)}
        container={document.fullscreenElement || document.body}
      />

      <PF2MAlertDialog
        hasCancel
        onClose={() => setIsDeallocationAlertOpen(false)}
        confirmText={translate('common:Yes')}
        description={translate('validation:ThisActionWillClearAllYourAllocations')}
        open={isDeallocationAlertOpen}
        onConfirm={() => {
          setIsDeallocationAlertOpen(false);
          dispatch(deallocateAllLoadAllocations());
        }}
      />
    </FullScreen>
  );
};

AllocationsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

AllocationsTab.defaultProps = {};

export default withStyles(styles)(AllocationsTab);
